// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { hidePrice } from "@sc/App/Store/Selectors/GeneralSelectors";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: ProductQuantityBreakPricingStyles;
}

export interface ProductQuantityBreakPricingStyles {
    viewLink?: LinkPresentationProps;
    modal?: ModalPresentationProps;
    table?: DataTableProps;
    tableHead?: DataTableHeadProps;
    breakQtyHeader?: DataTableHeaderProps;
    breakPriceHeader?: DataTableHeaderProps;
    savingsHeader?: DataTableHeaderProps;
    tableBody?: DataTableBodyProps;
    tableRow?: DataTableRowProps;
    breakQtyCell?: DataTableCellBaseProps;
    breakPriceCell?: DataTableCellBaseProps;
    savingsCell?: DataTableCellBaseProps;
}

export const productQuantityBreakPricingStyles: ProductQuantityBreakPricingStyles = {
    modal: {
        size: 400,
        cssOverrides: {
            modalContent: css`
                padding: 0;
            `,
            modalTitle: css`
                padding: 10px 10px 10px 20px;
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    breakQtyHeader: { alignX: "center" },
    breakPriceHeader: { alignX: "center" },
    savingsHeader: { alignX: "center" },
    breakQtyCell: { alignX: "center" },
    breakPriceCell: { alignX: "center" },
    savingsCell: { alignX: "center" },
};

const ProductQuantityBreakPricing: FC<OwnProps & HasProduct> = ({
    product,
    productInfo: { pricing },
    extendedStyles,
}) => {
    // XNG-Change: SC-1066
    const { showPricing, hidePrices } = useAppSelector((state: ApplicationState) => {
        return {
            showPricing: state.context.showPricing,
            hidePrices: hidePrice(state),
        };
    });

    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };
    const viewLinkClickHandler = () => {
        setModalIsOpen(true);
    };

    const [styles] = React.useState(() => mergeToNew(productQuantityBreakPricingStyles, extendedStyles));

    // XNG-Change: SC-1066
    if (
        !product ||
        product.quoteRequired ||
        !pricing ||
        !pricing.unitRegularBreakPrices ||
        pricing.unitRegularBreakPrices.length < 2 ||
        !showPricing ||
        hidePrices
    ) {
        return null;
    }

    // XNG-Change
    return (
        <>
            <Link
                aria-label={translate("View Quantity Break Pricing")}
                {...styles.viewLink}
                onClick={viewLinkClickHandler}
                data-test-selector="quantityBreakPricingLink"
            >
                {translate("View Quantity Break Pricing")}
            </Link>
            <Modal
                {...styles.modal}
                // XNG-Change
                headline={translate("Quantity Break Pricing")}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
                data-test-selector="quantityBreakPricingModal"
            >
                <DataTable {...styles.table}>
                    <DataTableHead {...styles.tableHead}>
                        <DataTableHeader {...styles.breakQtyHeader}>{translate("Min Qty")}</DataTableHeader>
                        <DataTableHeader {...styles.breakPriceHeader}>{translate("Price Per")}</DataTableHeader>
                        {/* XNG-Change */}
                        {/* <DataTableHeader {...styles.savingsHeader} title={translate("Savings")} /> */}
                    </DataTableHead>
                    <DataTableBody {...styles.tableBody}>
                        {pricing!.unitRegularBreakPrices!.map(breakPrice => (
                            <DataTableRow
                                {...styles.tableRow}
                                key={breakPrice.breakQty}
                                data-test-selector="breakPriceRow"
                            >
                                <DataTableCell {...styles.breakQtyCell} data-test-selector="breakQty">
                                    {breakPrice.breakQty}
                                </DataTableCell>
                                <DataTableCell {...styles.breakPriceCell} data-test-selector="price">
                                    {breakPrice.breakPriceDisplay}
                                </DataTableCell>
                                {/* XNG-Change */}
                                {/* <DataTableCell {...styles.savingsCell} data-test-selector="message">
                                    {breakPrice.savingsMessage}
                                </DataTableCell> */}
                            </DataTableRow>
                        ))}
                    </DataTableBody>
                </DataTable>
            </Modal>
        </>
    );
};

export default withProduct(ProductQuantityBreakPricing);
